/* eslint-disable import/no-cycle */
import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Anchor, List, Loader } from '@mantine/core';
import { AlertCircle } from 'tabler-icons-react';
import { Link } from 'react-router-dom';
import AppCard from '../../../../../common/AppCard';
import AppFlexbox from '../../../../../common/AppFlexbox';
import AppStack from '../../../../../common/AppStack';
import AppText from '../../../../../common/AppText';
import {
  isRegistrationUnavailable,
  isCartVariantUnavailable
} from '../../../../../../helpers/shop/webstoreHelper';
import { currencyFormat } from '../../../../../../helpers/format';
import { useMediaQueries } from '../../../../../../helpers/hooks';

const WsUnavailableProductsAlert = ({
  ecomStoreCartProducts,
  regCartProducts,
  latestVersionEcomStoreCartProducts,
  latestVersionRegAssociationDivisionForms,
  mediaQueries,
  baseStoreUrl,
  onNavigate,
  submitting,
  onRemoveUnavailableProducts,
  isDrawerView
}) => {
  const { isTabletOrSmaller } = mediaQueries || useMediaQueries();

  const ecomStoreCartWarnings =
    latestVersionEcomStoreCartProducts?.map((latestVariant) => {
      const cartProduct = ecomStoreCartProducts.find(
        (c) =>
          c.ecomStoreProductVariant.ecomVendorProductVariant.uuid ===
          latestVariant.ecomVendorProductVariant.uuid
      );

      const isStillUnavailable = isCartVariantUnavailable(latestVariant);

      const url = `${baseStoreUrl}?product=${cartProduct.ecomStoreProductVariant.ecomStoreProduct.uuid}&variant=${cartProduct.ecomStoreProductVariant.ecomVendorProductVariant.uuid}`;

      return {
        key: latestVariant.ecomVendorProductVariant.uuid,
        isStillUnavailable,
        url,
        productName:
          cartProduct.ecomStoreProductVariant.ecomStoreProduct.ecomVendorProduct
            .name,
        oldPrice: cartProduct.ecomStoreProductVariant.price,
        newPrice: latestVariant.price
      };
    }) || [];

  const regCartWarnings =
    latestVersionRegAssociationDivisionForms?.map((latestForm) => {
      const cartProduct = regCartProducts.find(
        (c) =>
          c.regFormSubmission.regAssociationDivisionForm.regAssociationDivision
            .pkRegAssociationDivision ===
            latestForm.regAssociationDivision.pkRegAssociationDivision &&
          c.regFormSubmission.regAssociationDivisionForm.regForm.uuid ===
            latestForm.regForm.uuid
      );

      const isStillUnavailable = isRegistrationUnavailable(latestForm);

      return {
        key: `${latestForm.regAssociationDivision.pkRegAssociationDivision}-${latestForm.regForm.uuid}`,
        isStillUnavailable,
        url: baseStoreUrl,
        productName: cartProduct.regFormSubmission.regAssociationDivisionForm
          .titleOverride
          ? cartProduct.regFormSubmission.regAssociationDivisionForm
              .titleOverride
          : cartProduct.regFormSubmission.regAssociationDivisionForm.regForm
              .name,
        oldPrice:
          cartProduct.regFormSubmission.regAssociationDivisionForm.regProduct
            .price,
        newPrice: latestForm.regProduct.price
      };
    }) || [];

  const cartWarnings = [...ecomStoreCartWarnings, ...regCartWarnings];

  return (
    cartWarnings.length > 0 && (
      <AppCard
        radius={isTabletOrSmaller ? 0 : 'md'}
        shadow="lg"
        style={{ padding: 0, width: '100%' }}
        withBorder
      >
        <Alert
          color="#eca70a"
          style={{ width: '100%' }}
          styles={{ root: { padding: isDrawerView ? 8 : 16 } }}
          variant="light"
        >
          <AppFlexbox style={{ gap: isDrawerView ? 8 : 16, width: '100%' }}>
            <AppStack>
              <AlertCircle color="#eca70a" size={isDrawerView ? 24 : 32} />
            </AppStack>
            <AppStack style={{ gap: 5, width: '100%' }}>
              <AppText
                style={{ fontSize: isDrawerView ? 16 : 20, fontWeight: 700 }}
              >
                Important {cartWarnings.length === 1 ? 'message' : 'messages'}
              </AppText>

              <AppStack
                style={{ gap: 5, flex: 1, width: '100%', paddingRight: 5 }}
              >
                <List style={{ width: '100%' }}>
                  {cartWarnings.map((warning) => (
                    <List.Item key={warning.key}>
                      {warning.isStillUnavailable ? (
                        <AppText
                          style={{
                            fontSize: 14,
                            width: '100%',
                            whiteSpace: 'break-spaces'
                          }}
                        >
                          Please note that{' '}
                          <Anchor
                            component={Link}
                            onClick={onNavigate}
                            style={{ fontSize: 14 }}
                            to={warning.url}
                            underline="hover"
                          >
                            {warning.productName}
                          </Anchor>{' '}
                          is <b>unavailable</b> for purchase. Update your cart
                          to remove this product.
                        </AppText>
                      ) : warning.newPrice !== warning.oldPrice ? (
                        <AppText style={{ fontSize: 14 }}>
                          The price of{' '}
                          <Anchor
                            component={Link}
                            onClick={onNavigate}
                            style={{ fontSize: 14 }}
                            to={warning.url}
                            underline="hover"
                          >
                            {warning.productName}
                          </Anchor>{' '}
                          has{' '}
                          <b>
                            {warning.newPrice > warning.oldPrice
                              ? 'increased'
                              : 'decreased'}
                          </b>{' '}
                          from <b>{currencyFormat(warning.oldPrice / 100)}</b>{' '}
                          to <b>{currencyFormat(warning.newPrice / 100)}</b>.
                          Update you cart to apply the new price.
                        </AppText>
                      ) : (
                        <AppText style={{ fontSize: 14 }}>
                          Please note that the details for{' '}
                          <Anchor
                            component={Link}
                            onClick={onNavigate}
                            style={{ fontSize: 14 }}
                            to={warning.url}
                            underline="hover"
                          >
                            {warning.productName}
                          </Anchor>{' '}
                          have changed. Update your cart to review the changes.
                        </AppText>
                      )}
                    </List.Item>
                  ))}
                </List>
              </AppStack>

              <AppFlexbox
                style={{ marginTop: 5, alignItems: 'center', gap: 8 }}
              >
                <AppText style={{ fontSize: 14, fontWeight: 500 }}>
                  Please review the details before proceeding, then{' '}
                  <Anchor
                    onClick={() => {
                      if (!submitting) {
                        onRemoveUnavailableProducts();
                      }
                    }}
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      color: submitting ? '#999' : 'dodgerblue'
                    }}
                    underline="always"
                  >
                    update your cart by clicking here.
                  </Anchor>{' '}
                </AppText>
                {submitting && <Loader size={14} />}
              </AppFlexbox>
            </AppStack>
          </AppFlexbox>
        </Alert>
      </AppCard>
    )
  );
};

WsUnavailableProductsAlert.propTypes = {
  baseStoreUrl: PropTypes.string,
  ecomStoreCartProducts: PropTypes.array,
  isDrawerView: PropTypes.bool,
  latestVersionEcomStoreCartProducts: PropTypes.array,
  latestVersionRegAssociationDivisionForms: PropTypes.array,
  mediaQueries: PropTypes.object,
  onNavigate: PropTypes.func,
  onRemoveUnavailableProducts: PropTypes.func,
  regCartProducts: PropTypes.array,
  submitting: PropTypes.bool
};

export default WsUnavailableProductsAlert;
